import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IConfigData } from '@df/catalog/config/config.interfaces';
import { CheckoutPaymentMethodsComponentDef } from '@df/checkout/checkout-payment-methods.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { ApplePayFilterPipe } from '@df/module-apple-pay/apple-pay-filter.pipe';
import { ApplePayService } from '@df/module-apple-pay/apple-pay.service';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { IfKlarnaPaymentDirective } from '@df/module-klarna/if-klarna-payment.directive';
import { Klarna } from '@df/module-klarna/klarna.namespace';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsConfig } from '@icms/core/resource/config/icms-config';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { CMS_CONFIG_CHECKOUT, ICmsConfigCheckoutDataContent } from '../cms/config/checkout.cms-config';
import { KlarnaPaymentComponent } from '../module/klarna/klarna-payment.component';
import { SagepayPiFormComponent } from '../module/sagepay/pi/sagepay-pi-form.component';
import { SagepayPiSavedCardComponent } from '../module/sagepay/pi/sagepay-pi-saved-card.component';
import { SagepayPiServiceDecorator } from '../module/sagepay/pi/sagepay.service.decorator';
import { Sagepay } from '../module/sagepay/sagepay.namespace';
import { ResultComponent } from '../ui/api/result.component';
import { InputWrapBodyDirective } from '../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../ui/form/input-wrap.component';
import { RadioComponent } from '../ui/form/radio';
import { CheckoutFormComponent } from './checkout-form.component';
import { CheckoutStepComponent } from './checkout-step.component';

@Component({
    selector: 'checkout-payment-methods',
    templateUrl: './checkout-payment-methods.html',
    styleUrl: './checkout-payment-methods.scss',
    standalone: true,
    imports: [
        ApplePayFilterPipe,
        CommonModule,
        FormsModule,
        IfKlarnaPaymentDirective,
        InputWrapBodyDirective,
        InputWrapComponent,
        KlarnaPaymentComponent,
        RadioComponent,
        RadioGroupDirective,
        ResultComponent,
        SagepayPiFormComponent,
        SagepayPiSavedCardComponent,
        SizeClassDirective,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutPaymentMethodsComponent extends CheckoutPaymentMethodsComponentDef implements OnInit {
    defaultMethod?: CreditCard;
    methodChanged = false;
    creditCardExpiredMessage?: string;

    constructor(
        checkoutFormComponent: CheckoutFormComponent,
        checkoutStepComponent: CheckoutStepComponent,
        ref: ChangeDetectorRef,
        protected sagepayPiService: SagepayPiServiceDecorator,
        private applePayService: ApplePayService,
        private cmsConfigResource: ICmsConfigResource
    ) {
        super(checkoutFormComponent, checkoutStepComponent, ref);
    }

    override ngOnInit(): void {
        this.cmsConfigResource.loadModelByUrl(CMS_CONFIG_CHECKOUT).then(() => {
            const config = this.cmsConfigResource.getModelByUrl(CMS_CONFIG_CHECKOUT) as ICmsConfig<
                IConfigData<ICmsConfigCheckoutDataContent>
            >;
            this.creditCardExpiredMessage = config?.data?.content?.creditCardExpiredMessage;
        });

        super.ngOnInit();
    }

    protected override onCheckoutChange(change: Checkout.IStateChange) {
        super.onCheckoutChange(change);

        const savedCards = this.checkoutFormComponent.paymentTokens;
        this.defaultMethod = savedCards.find(savedCard => savedCard.data.is_default);

        if (this.defaultMethod && [this.defaultMethod.code, 'paypal_express'].indexOf(`${this.data.method}`) === -1) {
            this.methodChanged = true;
        }

        this.detectChanges();
    }

    methodHasMore(method: Checkout.IPaymentMethod | CreditCard): boolean {
        if (method instanceof CreditCard) {
            return method.isCcvRequired();
        } else {
            const code = method.code as string;
            const methods = [
                Sagepay.Methods.PiForm,
                Braintree.Methods.ApplePay,
                Klarna.Methods.PAY_LATER,
                Klarna.Methods.PAY_OVER_TIME,
                Sagepay.Methods.ApplePay
            ] as string[];

            return methods.indexOf(code) > -1;
        }
    }

    getCardTitle(type: string) {
        return this.sagepayPiService.getCardTitle(type.toLowerCase());
    }

    protected override validateSelectedMethod(): void {
        super.validateSelectedMethod();

        if (
            this.data.method === Braintree.Methods.ApplePay &&
            (!this.applePayService.isAvailable || !this.applePayService.isSupportedVersion(3))
        ) {
            this.data.method = undefined;
        }
    }

    changeHandle() {
        this.data.method = undefined;
        this.defaultMethod = undefined;
        this.methodChanged = true;

        this.detectChanges();
    }
}
